import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Combobox,
  type ComboboxInputPropsInterface,
  DropdownSelect,
  FormField,
  FormHelp,
  Icon,
  Label,
  Modal,
  ToggleButton,
} from '@zillow/constellation-10';
import type { ComboboxOptionType } from '@zillow/constellation-10';
import { IconArrowRightOutline, IconClockOutline } from '@zillow/constellation-icons';
import type Constellation from '@zillow/constellation';
import { addWeeks, format, isBefore, parse } from 'date-fns';
import { RecurringFrequency, TourDuration } from '@zg-rentals/ts-z-calendar-client';
type DatePickerMetaProps = Constellation.DatePickerMetaProps;

import type {
  AvailabilityEvent,
  AvailabilityEventWeeklyRecurringDaysMap,
  OnCalendarEventSaveClickParams,
  RecurringFrequencyValues,
  WeeklyRecurringDays,
} from '../../../../constants/TourCalendar';
import {
  DefaultSelectedWeeklyRecurringDays,
  HOUR_MINUTE_MERIDIEM_FORMAT_STRING,
  RecurringFrequencyNeverValue,
  RecurringFrequencyValueLabels,
  RecurringWeeklyButtonsValueLabels,
  TOUR_DURATION_NUMBER_MAP,
} from '../../../../constants/TourCalendar';
import FormattedDatePickerInput from '../FormattedDatePickerInput';
import {
  calculateEndTimeWithPreservedDuration,
  convert12HourTimeTo24HourTime,
  createAvailabilityTimeOptions,
  isStartAndEndTimeFullHourBlock,
  isTimeBeforeOrEqual,
} from '../../../../utils/availabilityTimeUtils';
import ReccuringAvailabilityConfirmationModal from '../RecuringAvailabilityConfirmationModal';
import { formatTimeString, getTourDateAndTime, setTimeForDate } from '../../../../utils/formatTime';
import { FULL_HOUR_BLOCK_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';

import EventDetails from '../../../EventDetails';

import TourCalendarContext from '../../../TourCalendarWithSettings/context/TourCalendarContext';

const getTimeslotValidationError = ({
  selectedStartTime,
  selectedEndTime,
  tourDuration,
}: {
  selectedStartTime: string;
  selectedEndTime: string;
  tourDuration: TourDuration;
}) => {
  if (!selectedStartTime || !selectedEndTime) {
    return 'Enter a start and end time';
  }

  if (isTimeBeforeOrEqual(selectedEndTime, selectedStartTime)) {
    return 'End time must be after start time';
  }

  // 60 minute tours must have a duration that is a multiple of 60 minutes
  const parsedSelectedStartTime = parse(selectedStartTime, HOUR_MINUTE_MERIDIEM_FORMAT_STRING, new Date());
  const parsedSelectedEndTime = parse(selectedEndTime, HOUR_MINUTE_MERIDIEM_FORMAT_STRING, new Date());
  if (
    tourDuration === TourDuration.MINUTES_60 &&
    !isStartAndEndTimeFullHourBlock(parsedSelectedStartTime, parsedSelectedEndTime, tourDuration)
  ) {
    return FULL_HOUR_BLOCK_ERROR_MESSAGE;
  }
};

const getRecurringFormValidationError = ({
  selectedEventDate,
  selectedFrequency,
  selectedWeeklyRecurringDays,
  selectedRepeatUntilDate,
}: {
  selectedEventDate: string;
  selectedFrequency: RecurringFrequencyValues;
  selectedWeeklyRecurringDays: AvailabilityEventWeeklyRecurringDaysMap;
  selectedRepeatUntilDate: string;
}) => {
  const isRecurringWeeklySelected = selectedFrequency === RecurringFrequency.WEEKLY;
  const hasNoRecurringDaysSelected = !Object.values(selectedWeeklyRecurringDays).some(
    (isDayRecurring) => isDayRecurring,
  );

  if (isRecurringWeeklySelected && hasNoRecurringDaysSelected) {
    return 'Select at least one recurring day';
  }

  if (isRecurringWeeklySelected && isBefore(new Date(selectedRepeatUntilDate), new Date(selectedEventDate))) {
    return 'Date must be after event date.';
  }

  return null;
};

export type AvailabilityEventEditModalProps = {
  canEditTourDuration: boolean;
  children?: JSX.Element;
  event: AvailabilityEvent;
  header: React.ReactNode;
  isOpen?: boolean;
  tourDuration: TourDuration;
  onClose: () => void;
  onSaveClick: (params: OnCalendarEventSaveClickParams) => void;
};

const AvailabilityEventEditModal = ({
  canEditTourDuration,
  children,
  event,
  header,
  isOpen,
  tourDuration,
  onClose,
  onSaveClick,
}: AvailabilityEventEditModalProps) => {
  const { listingAddress, propertyPhotoUrl } = useContext(TourCalendarContext);
  const [selectedEventDate, setSelectedEventDate] = useState(format(event.start, 'MM/dd/yyyy'));
  const [selectedStartTime, setSelectedStartTime] = useState(formatTimeString(event.start, { showMeridiem: true }));
  const [selectedEndTime, setSelectedEndTime] = useState(formatTimeString(event.end, { showMeridiem: true }));
  const [selectedFrequency, setSelectedFrequency] = useState<RecurringFrequencyValues>(
    event.frequency || RecurringFrequencyNeverValue,
  );
  const [selectedWeeklyRecurringDays, setSelectedWeeklyRecurringDays] =
    useState<AvailabilityEventWeeklyRecurringDaysMap>(DefaultSelectedWeeklyRecurringDays);
  const [selectedRepeatUntilDate, setSelectedRepeatUntilDate] = useState(format(addWeeks(new Date(), 3), 'MM/dd/yyyy'));
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);

  const timeslotValidationError = useMemo(
    () => getTimeslotValidationError({ selectedStartTime, selectedEndTime, tourDuration }),
    [selectedStartTime, selectedEndTime, tourDuration],
  );
  const recurringFormValidationError = useMemo(
    () =>
      getRecurringFormValidationError({
        selectedEventDate,
        selectedFrequency,
        selectedWeeklyRecurringDays,
        selectedRepeatUntilDate,
      }),
    [selectedEventDate, selectedFrequency, selectedWeeklyRecurringDays, selectedRepeatUntilDate],
  );

  const startAvailabilityTimeOptions = useMemo(() => createAvailabilityTimeOptions(tourDuration), [tourDuration]);
  const endAvailabilityTimeOptions = useMemo(
    () => createAvailabilityTimeOptions(tourDuration, convert12HourTimeTo24HourTime(selectedStartTime)),
    [selectedStartTime, tourDuration],
  );
  const eventDateAndTime = useMemo(() => getTourDateAndTime(event.start, event.end), [event]);

  const isRecurringWeeklySelected = selectedFrequency === RecurringFrequency.WEEKLY;
  const isSaveDisabled = Boolean(timeslotValidationError) || Boolean(recurringFormValidationError);

  useEffect(() => {
    if (event.recurringDays) {
      setSelectedWeeklyRecurringDays(event.recurringDays);
    }
    if (event.frequency) {
      setSelectedFrequency(event.frequency);
    }
    if (event.recurrenceEndDateMs) {
      setSelectedRepeatUntilDate(format(new Date(event.recurrenceEndDateMs), 'MM/dd/yyyy'));
    }
  }, [event]);

  useEffect(() => {
    if (!event.isRecurringEvent && isRecurringWeeklySelected) {
      const eventDayName = format(parse(selectedEventDate, 'MM/dd/yyyy', new Date()), 'EEEE').toUpperCase();
      setSelectedWeeklyRecurringDays({ ...DefaultSelectedWeeklyRecurringDays, [eventDayName]: true });
    }
  }, [event, isRecurringWeeklySelected]);

  const onSaveClickHandler = () => {
    onSaveClick({
      selectedStartTime: convert12HourTimeTo24HourTime(selectedStartTime),
      selectedEndTime: convert12HourTimeTo24HourTime(selectedEndTime),
      selectedEventDate,
      selectedFrequency: selectedFrequency !== RecurringFrequencyNeverValue ? selectedFrequency : undefined,
      selectedWeeklyRecurringDays,
      selectedRepeatUntilDate,
    });
  };

  const handleSaveClick = () => {
    if (event.isRecurringEvent) {
      return setDisplayConfirmationModal(true);
    }

    onSaveClickHandler();
  };

  const onChangeStartTime: (startTime: ComboboxOptionType) => void = (startTime) => {
    if (typeof startTime === 'string') {
      const originalStartTime = setTimeForDate(
        new Date(selectedEventDate),
        convert12HourTimeTo24HourTime(selectedStartTime),
      );
      const originalEndTime = setTimeForDate(
        new Date(selectedEventDate),
        convert12HourTimeTo24HourTime(selectedEndTime),
      );
      const newStartTime = setTimeForDate(new Date(selectedEventDate), convert12HourTimeTo24HourTime(startTime));

      const newEndTime = calculateEndTimeWithPreservedDuration({
        originalStart: originalStartTime,
        originalEnd: originalEndTime,
        newStart: newStartTime,
        targetDate: new Date(selectedEventDate),
      });

      if (newEndTime) {
        setSelectedEndTime(format(newEndTime, HOUR_MINUTE_MERIDIEM_FORMAT_STRING));
      }

      setSelectedStartTime(startTime);
    }
  };

  const onChangeEndTime: (option: ComboboxOptionType) => void = (option) => {
    if (typeof option === 'string') {
      setSelectedEndTime(option);
    }
  };

  const onChangeEventDate = (value: string, meta: DatePickerMetaProps) => {
    if (meta.reason === 'manual input') {
      return;
    }
    setSelectedEventDate(value);
    // Reset the weekly recurring days when event date changes
    const eventDayName = format(parse(value, 'MM/dd/yyyy', new Date()), 'EEEE').toUpperCase();
    setSelectedWeeklyRecurringDays({ ...DefaultSelectedWeeklyRecurringDays, [eventDayName]: true });
  };

  const onChangeFrequency = (value: RecurringFrequencyValues) => {
    if (value) {
      setSelectedFrequency(value);
    }
  };

  const onClickWeeklyRecurringDays = (value: WeeklyRecurringDays) => {
    setSelectedWeeklyRecurringDays({ ...selectedWeeklyRecurringDays, [value]: !selectedWeeklyRecurringDays[value] });
  };

  const onChangeRepeatUntilDate = (value: string, meta: DatePickerMetaProps) => {
    if (meta.reason === 'manual input') {
      return;
    }
    setSelectedRepeatUntilDate(value);
  };

  const body = (
    <Box data-testid="AvailabilityEventEditModal" css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
      <EventDetails
        eventDateAndTime={eventDateAndTime}
        listingStreet={listingAddress || ''}
        photoUrl={propertyPhotoUrl}
        title={(event.title as string) || 'Available'}
      />
      <FormField
        label={<Label>Date</Label>}
        control={
          <FormattedDatePickerInput
            date={selectedEventDate}
            onChangeDate={onChangeEventDate}
            dataTestId="AvailabilityEventEditModal-EventDate-Input"
          />
        }
        data-testid="AvailabilityEventEditModal-DatePicker"
      />
      <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <FormField
          css={{ width: '100%' }}
          label={<Label>From</Label>}
          control={
            <DropdownSelect
              options={startAvailabilityTimeOptions}
              value={selectedStartTime}
              onOptionSelect={onChangeStartTime}
              renderAdornment={(adornmentProps) => {
                const { ref, ...adornmentPropsWithoutRef } = adornmentProps;
                return (
                  <Combobox.Adornment {...adornmentPropsWithoutRef}>
                    <Icon size="md" render={<IconClockOutline />} />
                  </Combobox.Adornment>
                );
              }}
            />
          }
          data-testid="AvailabilityEventEditModal-StartTime"
        />
        <Icon css={{ marginTop: 'loose', marginLeft: 'tighter', marginRight: 'tighter' }} size="md">
          <IconArrowRightOutline />
        </Icon>
        <FormField
          css={{ width: '100%' }}
          label={<Label>To</Label>}
          control={
            <DropdownSelect
              options={endAvailabilityTimeOptions}
              value={selectedEndTime}
              onOptionSelect={onChangeEndTime}
              renderAdornment={(adornmentProps) => {
                const { ref, ...adornmentPropsWithoutRef } = adornmentProps;
                return (
                  <Combobox.Adornment {...adornmentPropsWithoutRef}>
                    <Icon size="md" render={<IconClockOutline />} />
                  </Combobox.Adornment>
                );
              }}
            />
          }
          data-testid="AvailabilityEventEditModal-EndTime"
        />
      </Box>
      {timeslotValidationError && (
        <FormHelp error css={{ marginTop: 'xs' }} data-testid="AvailabilityEventEditModal-Error">
          {timeslotValidationError}
        </FormHelp>
      )}
      {canEditTourDuration && (
        <Alert role="status" tone="info" data-testid="AvailabilityEventEditModal-TourDurationAlert">
          Tour length is set to {TOUR_DURATION_NUMBER_MAP[tourDuration]} min.
        </Alert>
      )}
      <FormField
        label={<Label>Repeat</Label>}
        control={
          <DropdownSelect
            options={RecurringFrequencyValueLabels}
            value={selectedFrequency}
            onChange={onChangeFrequency}
            showLabelForValue
            fluidDropdown
            renderInput={(renderProps: ComboboxInputPropsInterface) => {
              const { ref, ...adornmentPropsWithoutRef } = renderProps;
              return (
                <Combobox.Input data-testid="AvailabilityEventEditModal-RepeatDropdown" {...adornmentPropsWithoutRef} />
              );
            }}
          />
        }
      />

      {isRecurringWeeklySelected && (
        <ButtonGroup
          aria-label="Toggle button group for each day of the week"
          data-testid="AvailabilityEventEditModal-WeeklyRecurringDays"
        >
          {RecurringWeeklyButtonsValueLabels.map((option) => (
            <ToggleButton
              key={option.value}
              selected={selectedWeeklyRecurringDays[option.value]}
              onClick={() => onClickWeeklyRecurringDays(option.value)}
              aria-label={option.value}
              data-testid={`AvailabilityEventEditModal-WeeklyRecurringDays-${option.value}`}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ButtonGroup>
      )}
      {selectedFrequency ? (
        <FormField
          label={<Label>Repeat until</Label>}
          control={
            <FormattedDatePickerInput
              date={selectedRepeatUntilDate}
              onChangeDate={onChangeRepeatUntilDate}
              dataTestId="AvailabilityEventEditModal-RepeatUntilDate-Input"
            />
          }
          error={Boolean(recurringFormValidationError)}
          description={
            <FormHelp>Select a date up to three weeks in the future. {recurringFormValidationError}</FormHelp>
          }
          data-testid="AvailabilityEventEditModal-RepeatUntilDate"
        />
      ) : null}
    </Box>
  );

  const footer = (
    <Box asChild css={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'default', width: '100%' }}>
      <ButtonGroup aria-label="edit event form buttons">
        <Button onClick={onClose} data-testid="AvailabilityEventEditModal-CancelBtn">
          Cancel
        </Button>
        <Button
          emphasis="filled"
          onClick={handleSaveClick}
          disabled={isSaveDisabled}
          data-testid="AvailabilityEventEditModal-SaveBtn"
        >
          Save
        </Button>
      </ButtonGroup>
    </Box>
  );

  return (
    <>
      <Modal
        body={body}
        dividers
        footer={footer}
        header={header}
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
        trigger={children}
      />

      {displayConfirmationModal && (
        <ReccuringAvailabilityConfirmationModal
          isForDelete={false}
          onClose={() => setDisplayConfirmationModal(false)}
          onConfirm={onSaveClickHandler}
        />
      )}
    </>
  );
};

export default AvailabilityEventEditModal;
